import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_TI, s_PB, s_GB } from '../../shared/styles';
import { getBizFeed, postToBizFeed, setBizPostVisibility, deleteBizFeedPost } from './businessApi';

const CAN_POST_ROLES = ['owner', 'admin', 'manager', 'hr_manager', 'accountant', 'bookkeeper'];
const CAN_PUBLISH_ROLES = ['owner', 'admin', 'manager'];

const timeAgo = (t) => {
  const s = Math.floor((Date.now() - new Date(t)) / 1000);
  if (s < 60)  return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}u`;
  return `${Math.floor(s/86400)}d`;
};

export default function BusinessFeedScreen({ businessId, myRole, myUid, lang, onShareToCommunity }) {
  const nl = lang === 'nl';
  const canPost    = CAN_POST_ROLES.includes(myRole);
  const canPublish = CAN_PUBLISH_ROLES.includes(myRole);

  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [form, setForm]       = useState({ text: '', visibility: 'private', img: null });
  const [saving, setSaving]   = useState(false);
  const [imgPreview, setImgPreview] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getBizFeed(businessId);
    if (Array.isArray(r)) setPosts(r);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const handleImg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setImgPreview(ev.target.result); setForm(f => ({ ...f, img: ev.target.result })); };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.text.trim() && !form.img) return;
    setSaving(true);
    const r = await postToBizFeed(businessId, { user_id: myUid, text: form.text, img: form.img, visibility: form.visibility });
    setSaving(false);
    if (r && !r.error) {
      setForm({ text: '', visibility: 'private', img: null });
      setImgPreview(null);
      setShowCompose(false);
      load();
    }
  };

  const toggleVisibility = async (post) => {
    const newVis = post.visibility === 'private' ? 'public' : 'private';
    await setBizPostVisibility(businessId, post.id, newVis);
    load();
  };

  const remove = async (post) => {
    if (!window.confirm(nl ? 'Post verwijderen?' : 'Delete post?')) return;
    await deleteBizFeedPost(businessId, post.id);
    load();
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ color: C.white, margin: 0, fontSize: 16, fontWeight: 700 }}>
          📢 {nl ? 'Team feed' : 'Team feed'}
        </h3>
        {canPost && (
          <button style={s_PB({ fontSize: 12, padding: '6px 14px', borderRadius: 10 })} onClick={() => setShowCompose(c => !c)}>
            {showCompose ? (nl ? 'Sluiten' : 'Close') : '+ ' + (nl ? 'Posten' : 'Post')}
          </button>
        )}
      </div>

      {/* Compose box */}
      {showCompose && (
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 20, border: `1px solid ${_T.cardBorderTop}` }}>
          <textarea
            value={form.text}
            onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
            placeholder={nl ? 'Schrijf een bericht voor je team…' : 'Write a message for your team…'}
            style={{ ...s_TI({ width: '100%', minHeight: 80, resize: 'vertical', fontSize: 14, lineHeight: 1.5 }) }}
          />
          {imgPreview && (
            <div style={{ position: 'relative', marginTop: 8 }}>
              <img src={imgPreview} alt="" style={{ width: '100%', borderRadius: 10, maxHeight: 200, objectFit: 'cover' }} />
              <button onClick={() => { setImgPreview(null); setForm(f => ({ ...f, img: null })); }}
                style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', color: '#fff', width: 24, height: 24, cursor: 'pointer', fontSize: 14, lineHeight: '24px', textAlign: 'center' }}>×</button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button onClick={() => fileRef.current?.click()} style={s_GB({ fontSize: 12, padding: '6px 12px', borderRadius: 8 })}>
              📷 {nl ? 'Afbeelding' : 'Image'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImg} />

            {canPublish && (
              <div style={{ display: 'flex', gap: 4 }}>
                {['private', 'public'].map(vis => (
                  <button key={vis} onClick={() => setForm(f => ({ ...f, visibility: vis }))}
                    style={{ fontSize: 11, padding: '5px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
                      background: form.visibility === vis ? _T.accent : _T.bg4,
                      color: form.visibility === vis ? '#000' : C.silver,
                    }}>
                    {vis === 'private' ? (nl ? '🔒 Intern' : '🔒 Private') : (nl ? '🌐 Publiek' : '🌐 Public')}
                  </button>
                ))}
              </div>
            )}

            <button onClick={submit} disabled={saving || (!form.text.trim() && !form.img)}
              style={{ ...s_PB({ fontSize: 12, padding: '6px 14px', borderRadius: 8 }), marginLeft: 'auto', opacity: saving ? 0.6 : 1 }}>
              {saving ? '…' : (nl ? 'Plaatsen' : 'Post')}
            </button>
          </div>
        </div>
      )}

      {loading && <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>…</div>}

      {!loading && posts.length === 0 && (
        <div style={{ color: C.silver, textAlign: 'center', padding: 32, fontSize: 14 }}>
          {nl ? 'Nog geen berichten. Wees de eerste!' : 'No posts yet. Be the first!'}
        </div>
      )}

      {posts.map(post => {
        const isOwn = String(post.userId) === String(myUid);
        const canManage = isOwn || ['owner', 'admin'].includes(myRole);
        return (
          <div key={post.id} style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            {/* Post header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {post.user?.picture
                ? <img src={post.user.picture} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 34, height: 34, borderRadius: '50%', background: post.user?.color || _T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#000', flexShrink: 0 }}>{(post.user?.name || '?')[0]}</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{post.user?.name}</div>
                <div style={{ color: C.silver, fontSize: 11 }}>{timeAgo(post.time)}</div>
              </div>
              {/* Visibility badge */}
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                background: post.visibility === 'public' ? `${_T.accent}20` : `${C.silver}20`,
                color: post.visibility === 'public' ? _T.accent : C.silver }}>
                {post.visibility === 'public' ? (nl ? '🌐 Publiek' : '🌐 Public') : (nl ? '🔒 Intern' : '🔒 Private')}
              </span>
            </div>

            {/* Content */}
            {post.img && <img src={post.img} alt="" style={{ width: '100%', borderRadius: 10, marginBottom: 10, objectFit: 'cover', maxHeight: 280 }} />}
            {post.text && <div style={{ color: C.white, fontSize: 14, lineHeight: 1.6, marginBottom: 10 }}>{post.text}</div>}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: `1px solid ${_T.cardBorderBot}33` }}>
              {canPublish && canManage && (
                <button onClick={() => toggleVisibility(post)}
                  style={s_GB({ fontSize: 11, padding: '4px 10px', borderRadius: 8 })}>
                  {post.visibility === 'private' ? (nl ? '🌐 Publiceer' : '🌐 Make public') : (nl ? '🔒 Intern maken' : '🔒 Make private')}
                </button>
              )}
              {onShareToCommunity && (
                <button onClick={() => onShareToCommunity(post)}
                  style={s_GB({ fontSize: 11, padding: '4px 10px', borderRadius: 8 })}>
                  🔁 {nl ? 'Delen' : 'Share'}
                </button>
              )}
              {canManage && (
                <button onClick={() => remove(post)}
                  style={{ ...s_GB({ fontSize: 11, padding: '4px 10px', borderRadius: 8 }), borderColor: '#ef444444', color: '#ef4444', marginLeft: 'auto' }}>
                  🗑
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
